/** Structural identity formats shared by transcript admission and lineage. */
const DERIVED_TRANSCRIPT_IDENTITY_PREFIXES = [
  'segment-id:v1:',
  'segment-key:v1:',
  'legacy:v1:',
] as const;

/**
 * True only for a producer-supplied transcript identity.
 *
 * This is a check on a STRUCTURAL FORMAT this application mints itself, not a
 * semantic reading of a string: the prefixes are exactly the three literals
 * `transcriptSyncIdentity` composes.
 */
export function isJoinableTranscriptIdentity(syncId: string | undefined | null): syncId is string {
  if (!syncId) return false;
  return !DERIVED_TRANSCRIPT_IDENTITY_PREFIXES.some((prefix) => syncId.startsWith(prefix));
}


/** Format actually minted by TranscriptionHandler._bind_segment_sync_id. */
export function isMintedTranscriptIdentity(syncId: string | undefined | null): syncId is string {
  return typeof syncId === 'string' &&
    /^lt-sync:v1:[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(syncId);
}

interface TranscriptIdentityCarrier {
  syncId?: string;
  participantIdentity?: string;
  name: string;
  segmentId?: string;
  segmentKey?: string;
}

export function transcriptIdentityAliases(message: TranscriptIdentityCarrier): string[] {
  const speaker = message.participantIdentity || message.name || 'unknown';
  return [
    ...(message.syncId ? [JSON.stringify(['sync-id', message.syncId])] : []),
    ...(message.segmentId ? [JSON.stringify([speaker, 'segment-id', message.segmentId])] : []),
    ...(message.segmentKey ? [JSON.stringify([speaker, 'segment-key', message.segmentKey])] : []),
  ];
}

/**
 * Raw metadata may recover a legacy alias, but cannot override two conflicting
 * actual producer identities. A reused token on distinct raw segments also
 * cannot establish one utterance: retain both rows and let lineage refuse it.
 */
export function transcriptIdentitiesCanMatch(a: TranscriptIdentityCarrier, b: TranscriptIdentityCarrier): boolean {
  const speakerA = a.participantIdentity || a.name || 'unknown';
  const speakerB = b.participantIdentity || b.name || 'unknown';
  if (speakerA !== speakerB) return false;
  const mintedA = isMintedTranscriptIdentity(a.syncId);
  const mintedB = isMintedTranscriptIdentity(b.syncId);
  // Unknown legacy aliases do not attest a newly minted producer. Preserve
  // both actual records instead of losing the token behind an immutable alias.
  if (mintedA !== mintedB) return false;
  if (mintedA && mintedB) {
    if (a.syncId !== b.syncId) return false;
    if (a.segmentId && b.segmentId && a.segmentId !== b.segmentId) return false;
    if (!a.segmentId && !b.segmentId && a.segmentKey && b.segmentKey && a.segmentKey !== b.segmentKey) return false;
  }
  const aliases = new Set(transcriptIdentityAliases(a));
  return transcriptIdentityAliases(b).some(key => aliases.has(key));
}
